import React, { useEffect, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Typography } from '@astrid/components'
import { StudyGoalTimer } from 'components/StudyGoalTimer'
import { Shelf } from 'components/Shelf'
import { RecommendedItem } from 'components/RecommendedItem'
import useThunkDispatch from 'hooks/useThunkDispatch'
import { routes } from 'routes/RouterStackComponent'
import {
  asyncGetStudyTime,
  getLibrary,
  getLibraryShelfSectionPage,
  getShelfKey,
  selectLibraryContent,
  ShelfSection,
  StoreRecommendedItem
} from 'store/services/Library'
import { asyncGetCurrent, selectCurrentUser } from 'store/services/User'
import { LearningJourneyEntityType } from 'store/services/Journey'
import { asyncGetAllBooks, asyncGetBooksProgress, BookMode, BookType, setCurrentBookInfo } from 'store/services/Book'
import { GACategory, GAEvent, trackEvent } from 'utils/analytics'

import './Dashboard.scss'

const Dashboard: React.FC = () => {
  const isFetchingMorePaginatedData = useRef(false)
  const shelfRefs = useRef<(HTMLElement | null)[]>([])

  const { recommendedItem, shelves } = useSelector(selectLibraryContent)
  const user = useSelector(selectCurrentUser)

  const history = useHistory()
  const dispatch = useThunkDispatch()

  useEffect(() => {
    try {
      dispatch(asyncGetCurrent())
    } catch (error) {}
    dispatch(asyncGetAllBooks())
    dispatch(asyncGetBooksProgress())
    dispatch(asyncGetStudyTime(Intl.DateTimeFormat().resolvedOptions().timeZone))
    dispatch(getLibrary())
    // eslint-disable-next-line
  }, [])

  const handleBookClick = useCallback(
    (item: any, bookMode: BookMode) => {
      const bookId = item.data.bookId
      const bookTitle = item.data.title

      trackEvent({
        event: GAEvent.BOOK_ENTER,
        category: GACategory.BOOK,
        label: 'Book clicked on carousel',
        book_title: bookTitle
      })

      dispatch(setCurrentBookInfo(bookId, bookMode!))
      history.push(`/book/${bookMode}/${bookId}`)
    },
    // eslint-disable-next-line
    [history]
  )

  const handleLevelUpClick = useCallback(
    (item: any) => {
      const route = item.data.subtype === 'sentence' ? routes.sentence_builder : routes.word_craft
      history.push(`${routes.exercises}/${route}`)
    },
    // eslint-disable-next-line
    [history]
  )

  const handleItemClick = useCallback(
    (item: any, bookMode?: BookMode) => {
      switch (item.type) {
        case LearningJourneyEntityType.Book: {
          return handleBookClick(item, bookMode!)
        }
        case LearningJourneyEntityType.LevelUp: {
          return handleLevelUpClick(item)
        }
      }
    },
    // eslint-disable-next-line
    []
  )

  const handleRecommendedItemClick = useCallback((item: StoreRecommendedItem) => {
    if (!item) return

    const bookMode =
      item.type === LearningJourneyEntityType.Book
        ? item.data.type === BookType.Listening
          ? BookMode.LISTEN
          : BookMode.READ
        : undefined

    return handleItemClick(item, bookMode)
    // eslint-disable-next-line
  }, [])

  const onShelfSectionEndReached = async (shelfSection: ShelfSection) => {
    if (!shelfSection.page.hasAfter || isFetchingMorePaginatedData.current) return

    try {
      isFetchingMorePaginatedData.current = true
      await dispatch(getLibraryShelfSectionPage(shelfSection.id, { after: shelfSection.page.token }))
    } catch (error) {
    } finally {
      isFetchingMorePaginatedData.current = false
    }
  }

  const focusShelf = (index: number) => {
    ;(shelfRefs.current[index]?.querySelector('[tabindex="0"]') as HTMLElement)?.focus()
  }

  return (
    <main className="dashboard" aria-labelledby="dashboard-heading">
      <h1 id="dashboard-heading" style={{ position: 'absolute', opacity: 0, zIndex: -1 }}>
        Dashboard
      </h1>
      <StudyGoalTimer />
      {recommendedItem ? (
        <RecommendedItem item={recommendedItem} onClick={handleRecommendedItemClick} />
      ) : (
        <Typography style={{ margin: 0, fontWeight: 600 }} variant="h2">
          Welcome {user?.profile?.firstName || ''}!
        </Typography>
      )}
      {shelves.map((shelf, index) => (
        <Shelf
          shelfRef={(element) => (shelfRefs.current[index] = element)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              focusShelf(index + 1)
            }

            if (event.key === 'ArrowUp') {
              event.preventDefault()
              focusShelf(index - 1)
            }
          }}
          key={getShelfKey(shelf)}
          shelf={shelf}
          handleItemClick={handleItemClick}
          onShelfSectionEndReached={onShelfSectionEndReached}
        />
      ))}
    </main>
  )
}

export default Dashboard
